// Source of truth for `MeasurementUnit`, mirrored 1:1 from the Prisma enum in
// `backend/prisma/schema.prisma`. Kept as a plain string union (not imported
// from `@prisma/client`, which `shared` cannot depend on) so it is reusable
// from both `@IsIn(MEASUREMENT_UNITS)` DTO decorators and frontend code.
//
// Adding a value to the Prisma enum without adding it here (and classifying
// its genus below) is caught by `measurement-unit.test.ts`, which compares
// this array against a verbatim copy of the schema enum values.
export const MEASUREMENT_UNITS = [
  'KG',
  'G',
  'HG',
  'LITRI',
  'ML',
  'CL',
  'PZ',
  'CONF',
  'SCATOLE',
  'BOTTIGLIE',
  'BUSTE',
  'VASCHETTE',
  'BARATTOLI',
  'LATTINE',
  'SACCHI',
  'CASSETTE',
  'BANCALI',
  'M',
  'CM',
  'MQ',
  'MC',
] as const;

export type MeasurementUnit = (typeof MEASUREMENT_UNITS)[number];

// The pipeline reasons by genus, not by the 21 individual unit values — see
// "Semantica delle unita'" in the label plan. Each genus drives a different
// weight-suggestion strategy downstream (MASS -> computed weightG, VOLUME ->
// density lookup, COUNT -> parsed format, CONTAINER/DIMENSION -> none).
export const MEASUREMENT_UNIT_GENUSES = [
  'MASS',
  'VOLUME',
  'COUNT',
  'CONTAINER',
  'DIMENSION',
] as const;

export type MeasurementUnitGenus = (typeof MEASUREMENT_UNIT_GENUSES)[number];

// A `Record<MeasurementUnit, MeasurementUnitGenus>` forces this object to
// list all 21 keys at compile time: removing or adding a `MeasurementUnit`
// without updating this map is a TypeScript error, not a runtime surprise.
const GENUS_BY_MEASUREMENT_UNIT: Record<MeasurementUnit, MeasurementUnitGenus> = {
  G: 'MASS',
  HG: 'MASS',
  KG: 'MASS',
  ML: 'VOLUME',
  CL: 'VOLUME',
  LITRI: 'VOLUME',
  PZ: 'COUNT',
  CONF: 'COUNT',
  BUSTE: 'COUNT',
  BOTTIGLIE: 'COUNT',
  VASCHETTE: 'COUNT',
  BARATTOLI: 'COUNT',
  LATTINE: 'COUNT',
  SCATOLE: 'CONTAINER',
  SACCHI: 'CONTAINER',
  CASSETTE: 'CONTAINER',
  BANCALI: 'CONTAINER',
  M: 'DIMENSION',
  CM: 'DIMENSION',
  MQ: 'DIMENSION',
  MC: 'DIMENSION',
};

export function measurementUnitGenus(unit: MeasurementUnit): MeasurementUnitGenus {
  return GENUS_BY_MEASUREMENT_UNIT[unit];
}

// Grams per unit, exact for the three MASS units. `null` for every other
// genus: callers must branch on `measurementUnitGenus` rather than relying on
// a numeric default, since a container or dimension unit has no mass factor.
const MASS_FACTOR_GRAMS_BY_UNIT: Partial<Record<MeasurementUnit, number>> = {
  G: 1,
  HG: 100,
  KG: 1000,
};

export function massFactorFor(unit: MeasurementUnit): number | null {
  return MASS_FACTOR_GRAMS_BY_UNIT[unit] ?? null;
}

// Millilitres per unit, exact for the three VOLUME units. `null` elsewhere,
// mirroring `massFactorFor`.
const MILLILITRE_FACTOR_BY_UNIT: Partial<Record<MeasurementUnit, number>> = {
  ML: 1,
  CL: 10,
  LITRI: 1000,
};

export function millilitresFactorFor(unit: MeasurementUnit): number | null {
  return MILLILITRE_FACTOR_BY_UNIT[unit] ?? null;
}
